import { createBrowserClient } from '@supabase/ssr';

const LS_PREFIX = 'sb-session-';

function getFromLocalStorage(): { name: string; value: string }[] {
  if (typeof window === 'undefined') return [];
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(LS_PREFIX))
      .map((k) => ({ name: k.slice(LS_PREFIX.length), value: localStorage.getItem(k) ?? '' }))
      .filter((c) => c.value);
  } catch {
    return [];
  }
}

function getFromCookies(): { name: string; value: string }[] {
  if (typeof document === 'undefined') return [];
  try {
    return document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => {
        const eqIdx = c.indexOf('=');
        if (eqIdx < 0) return null;
        const name = c.slice(0, eqIdx).trim();
        // Do NOT decode — values are stored raw so the middleware reads them correctly
        const value = c.slice(eqIdx + 1).trim();
        return name ? { name, value } : null;
      })
      .filter((c): c is { name: string; value: string } => c !== null);
  } catch {
    return [];
  }
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          // Merge cookies + localStorage so session is always found
          const cookieMap = new Map<string, string>();
          getFromCookies().forEach(({ name, value }) => cookieMap.set(name, value));
          // localStorage values supplement (don't override) cookies
          getFromLocalStorage().forEach(({ name, value }) => {
            if (!cookieMap.has(name)) cookieMap.set(name, value);
          });
          return Array.from(cookieMap.entries()).map(([name, value]) => ({ name, value }));
        },
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              // Write raw value — do NOT encodeURIComponent.
              // The Supabase SSR library manages its own encoding internally.
              // Encoding here causes the middleware's createServerClient to receive
              // garbled tokens, making getUser() return null and triggering 307 redirects.
              let cookieStr = `${name}=${value}; Path=${(options as any)?.path ?? '/'}; SameSite=Lax`;
              if ((options as any)?.maxAge) cookieStr += `; Max-Age=${(options as any).maxAge}`;
              if (process.env.NODE_ENV === 'production') cookieStr += '; Secure';
              document.cookie = cookieStr;
              // Also write to localStorage as reliable fallback for iframe environments
              try {
                localStorage.setItem(`${LS_PREFIX}${name}`, value);
              } catch {
                // localStorage unavailable — cookie-only mode
              }
            } else {
              // Delete from both
              document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
              try {
                localStorage.removeItem(`${LS_PREFIX}${name}`);
              } catch {
                // ignore
              }
            }
          });
        },
      },
    }
  );
}
